import React from 'react';
import { useExtendableListPagination } from '@cp/shared/pagination';
import { CATALOG_FEEDBACK_PAGINATION } from '@common/const/catalog/feedback';
import { FeedbackViewAuthorName, FeedbacksList, FeedbacksListProps } from '@cp/entities/feedback';
import { Link } from '@cp/ds/src/components/link';
import { CITY_PATH_PARAMETER, useClientWebResolver } from '@cp/entities/clientWebRoutes';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { coreCatalogItemApi } from '@cp/entities/catalogItem';
import { coreCatalogFeedbacksApi } from 'src/entities/catalog';
import { CatalogFeedbacksProps } from './types';

const RenderFeedbackItem: FeedbacksListProps['renderItem'] = ({ Component, props }) => {
  const resolve = useClientWebResolver();
  const subjectCatalogItemId = props.data.subjectCatalogItemId;

  const { data: catalogItem } = coreCatalogItemApi.endpoints.getById.useQuery(
    { id: subjectCatalogItemId ?? -1 },
    { skip: !subjectCatalogItemId },
  );

  const permalink = catalogItem
    ? resolve
        .resolve({
          page: ClientWebPage.CatalogItem,
          pageProps: { catalogType: catalogItem.type, careerId: catalogItem.careerId },
          pathParams: { id: catalogItem.id, [CITY_PATH_PARAMETER]: catalogItem.city?.alias },
        })
        .toString()
    : undefined;

  return (
    <Component
      {...props}
      slots={{
        authorName: (authorNameProps) => (
          <Link disableHover external={Boolean(permalink)} href={permalink}>
            <FeedbackViewAuthorName {...authorNameProps} />
          </Link>
        ),
      }}
    />
  );
};

export const CatalogFeedbacks: React.FC<CatalogFeedbacksProps> = ({ catalogType, careerId, cityId, productName }) => {
  const { items, isLoading, canLoadMore, onLoadMore, additionalData } = useExtendableListPagination({
    parameters: {
      type: catalogType,
      cityId,
      careerId,
      pagination: CATALOG_FEEDBACK_PAGINATION,
    },
    query: coreCatalogFeedbacksApi.endpoints.getList,
  });

  const { averageRating, totalCount } = additionalData ?? {};

  const viewProps: FeedbacksListProps = {
    productName,
    totalCount,
    averageRating,
    items,
    isLoading,
    onLoadMore,
    canLoadMore,
    itemLayoutType: 'card',
    hideTitle: true,
  };

  return <FeedbacksList {...viewProps} renderItem={RenderFeedbackItem} />;
};
